import { inject, observer } from 'mobx-react';
import React from 'react';
import { Col, Row, Card, Alert, Accordion } from 'react-bootstrap';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import Store from '../../../store';
import Error from '../../components/error/Error';

import FunnelBreakdownComponent from '../../components/performance_check/FunnelBreakdownComponent';
import LeadToRevenueComponent from '../../components/performance_check/LeadToRevenueComponent';
import ModelPerformanceGraphs from '../../components/ModelPerformanceGraphs';
import ModelPerformanceBands from '../../components/model/ModelPerformanceBands';
import { unitOf } from '../../components/model/segmentVocabulary';
import PerformanceBlocks from '../../components/model/PerformanceBlocks';
import PerformanceSamples from '../../components/model/PerformanceSamples';
import {
  fetchLivePerformance,
  LivePerformance,
} from '../../components/model/livePerformanceApi';
import { TIERS } from '../../components/trees/treePerformance';
import '../../components/trees/segmented.css';
import './performanceCheck.css';
import Spinner from '../../components/Spinner';
import PredictedLeadValue from '../../components/performance_check/PredictedLeadValue';

/** The validation build's row cap. Sitting exactly on it means it sampled. */
const VALIDATION_ROW_CAP = 100000;

interface PerformanceCheckPageProps extends RouteComponentProps {
  store?: Store;
  /**
   * The redesigned page. Opt in, and only the flagged Review route opts in,
   * because this route renders in both flag states and the off one has to keep
   * rendering what it renders today.
   */
  revamped?: boolean;
}

type PerformanceCheckPageState = {
  isLoading: boolean;
  /**
   * The model as running, from the same data the Predictions pages read.
   *
   * Null once the fetch has settled without one, which is how "not deployed",
   * "deployed and not processed yet" and "bongo did not answer" all end up on
   * the validation source with the live half of the switch disabled.
   */
  live: LivePerformance | null;
  /** Undefined until the fetch settles, so the switch does not flicker. */
  liveResolved: boolean;
  source: 'validation' | 'live';
};

const PerformanceCheckPage = inject('store')(
  observer(
    class PerformanceCheckPage extends React.Component<
      PerformanceCheckPageProps,
      PerformanceCheckPageState
    > {
      scrollAnchor: React.RefObject<HTMLDivElement>;

      constructor(props: PerformanceCheckPageProps) {
        super(props);
        this.state = {
          isLoading: true,
          live: null,
          liveResolved: false,
          source: 'validation',
        };

        this.executeScroll = this.executeScroll.bind(this);
      }

      async componentDidMount() {
        await this.props.store.loadValidationData({ isProcessing: true });

        if (this.props.store.activeModel.type === 'customer_fit') {
          await this.props.store.loadPerformanceCheck();
        }
        this.setState({
          isLoading: false,
        });

        // After the page is up, not before it. The validation source is what
        // this page is, and it must not wait on a second service to draw.
        const { activeModel, tenant, modelId } = this.props.store;
        if (!this.props.revamped) return;
        // An engagement model has no live series to read yet. Resolved, so the
        // switch does not sit on "Looking for live numbers…" for ever.
        if (activeModel.type !== 'customer_fit') {
          this.setState({ live: null, liveResolved: true });
          return;
        }

        // A model that is not deployed has no production to read, so there is
        // nothing to ask for. Resolved rather than left pending, or the
        // switch would sit on "Looking for live numbers…" forever on every
        // draft. The server refuses the same case on bongo's own `live` flag,
        // which is the one that knows whether production is scoring it; this
        // is here to save the round trip, not to be the gate.
        if (!activeModel.live) {
          this.setState({ live: null, liveResolved: true });
          return;
        }

        const live = await fetchLivePerformance(tenant, modelId, TIERS);
        this.setState({ live, liveResolved: true });
      }

      executeScroll() {
        this.scrollAnchor.current.scrollIntoView();
      }

      render() {
        const { validationPage, performanceCheckPage, activeModel } =
          this.props.store;
        const { revamped } = this.props;
        const isCustomerFit = activeModel.type === 'customer_fit';
        // Summed from the repartition rather than fetched: the counts are there
        // because the ensembling path now carries them, and the cap warning
        // needs a real number to compare against.
        const validationPopulation = Object.values(
          validationPage.validation?.performances?.repartition ?? {}
        ).reduce((sum, row) => sum + (Number(row?.leadsAbsolute) || 0), 0);

        const { performanceCheckError, performanceCheckData } =
          performanceCheckPage;

        // `isLive` is the guard, not `source`. The switch cannot be set to
        // live while `live` is null, but a reload could arrive between the two
        // and reading `live.funnel` off that is the one crash this page has.
        const { live } = this.state;
        const isLive = this.state.source === 'live' && !!live;

        const { validation } = validationPage;

        if (this.state.isLoading) {
          return (
            <>
              <Spinner />
              <div className="text-center">
                Calculating the performance of your model... This can take a few
                seconds to a few minutes depending on the volume of data to
                process
              </div>
            </>
          );
        }

        return (
          <div className="mt-3 mb-5">
            {performanceCheckError && <Error message={performanceCheckError} />}
            {validation.error && <Error message={validation.error} />}
            <>
              {revamped ? (
                <Card className="pchk-card">
                  <Card.Body>
                    <h5 className="pchk-title">
                      <i
                        className="fas fa-chart-column pchk-icon"
                        aria-hidden="true"
                      />
                      {isLive
                        ? 'Performance as running'
                        : activeModel.live
                        ? 'Performance as trained'
                        : 'Performance before deploying'}
                    </h5>
                    {/* Says which population these numbers describe, because that
                        is the one thing a reader assumes and cannot see. On a
                        live model the assumption is production, and until the
                        live source is picked it is wrong. */}
                    <p className="pchk-scope">
                      {isLive
                        ? `Scored in production over the last ${
                            live.periods.length
                          } ${
                            live.periods.length === 1 ? 'period' : 'periods'
                          }, counting ${live.conversionType} on the ${
                            live.audience
                          } audience. The same numbers the Predictions pages draw, so the two agree by construction.`
                        : 'From the validation dataset, which is held apart from the training data the model was configured on. Frozen when the dataset was built, so these numbers do not move on their own.'}
                    </p>

                    {/* The source switch the design puts above the blocks. Live
                        is shown rather than hidden even when it cannot answer,
                        and disabled rather than inert: the page has two sources
                        and a reader is entitled to know one exists and why it
                        is not answering. */}
                    <div className="pchk-src">
                      <div className="seg" role="group" aria-label="Source">
                        <button
                          type="button"
                          className={`seg-item ${isLive ? '' : 'seg-item--on'}`}
                          aria-pressed={!isLive}
                          onClick={() =>
                            this.setState({ source: 'validation' })
                          }
                        >
                          Validation dataset
                        </button>
                        <button
                          type="button"
                          className={`seg-item ${isLive ? 'seg-item--on' : ''}`}
                          aria-pressed={isLive}
                          disabled={!live}
                          onClick={() => this.setState({ source: 'live' })}
                        >
                          Live, last 6 months
                        </button>
                      </div>
                      {!live && (
                        <span className="pchk-src-note">
                          {!this.state.liveResolved
                            ? 'Looking for live numbers…'
                            : activeModel.live
                            ? 'This model is live but has not been scored in production yet, so there is nothing to read.'
                            : 'Not deployed, so there is no live source to read.'}
                        </span>
                      )}
                    </div>

                    {/* Only when it is true, and computed rather than assumed.
                        The validation build keeps every converter then samples
                        the rest up to the cap, so a population sitting exactly
                        on it means the rates below run high by an unknown
                        factor. */}
                    {!isLive && validationPopulation === VALIDATION_ROW_CAP && (
                      <p className="pchk-warn">
                        The validation population sits exactly on the{' '}
                        {VALIDATION_ROW_CAP.toLocaleString('en-US')} row cap,
                        which keeps every converter and samples the rest. The
                        rates below run high by an unknown factor.
                      </p>
                    )}
                  </Card.Body>
                </Card>
              ) : (
                <Card border="light" className="mt-4 mb-4">
                  <Card.Body>
                    <Card.Text>
                      Before deploying your model, review the performance of the
                      model. This is done on the validation dataset that is
                      different from the one used to configure the model (the
                      training dataset). The population and conversions here are
                      as defined in the validation dataset parameters.
                    </Card.Text>
                  </Card.Body>
                </Card>
              )}
              {!!validation.performances &&
                (revamped && isCustomerFit ? (
                  /* The same charts as Fine tuning and the tree page. Customer
                     fit only: the band colours come from the fit palette, and a
                     behavioural model's segments are not those four. */
                  <>
                    <PerformanceBlocks
                      funnel={
                        isLive
                          ? live.funnel
                          : performanceCheckData?.funnelBreakdown ?? null
                      }
                      performances={validation.performances}
                      /* Live carries its own rates: they are accrued over the
                         window rather than derived from a repartition, and
                         there is no repartition of production to derive them
                         from. */
                      rates={
                        isLive
                          ? TIERS.map((tier) => live.rates[tier] ?? 0)
                          : null
                      }
                      distributionBasis={
                        isLive
                          ? `${live.conversionType}, scored in production`
                          : 'as labelled in the validation dataset'
                      }
                      rateBasis={
                        isLive
                          ? `${live.conversionType} only`
                          : 'Closed Won only'
                      }
                      rateNote={
                        isLive
                          ? 'Scaled against the best converting tier, so the top bar fills its track. These are production rates on the whole scored population, so the absolute numbers are readable as well as the relative ones.'
                          : 'Scaled against the best converting tier, so the top bar fills its track. The relative difference is the reading, not the absolute rate: the validation dataset is rebalanced.'
                      }
                    />
                    <Card className="pchk-card">
                      <Card.Body>
                        {/* Title and cohort switch are the panel's own header,
                            on one line. The scope line that sat here said again
                            what the source note at the top of the page already
                            says, and it separated the switch from the heading it
                            belongs to. */}
                        <PerformanceSamples
                          funnel={performanceCheckData?.funnelBreakdown ?? null}
                        />
                      </Card.Body>
                    </Card>
                  </>
                ) : revamped ? (
                  /* An engagement model under the revamp: the same band charts
                     in the engagement vocabulary. The funnel and samples blocks
                     stay fit only; they read the fit performance check, which
                     this page does not load for the other types. */
                  <ModelPerformanceBands
                    performances={validation.performances}
                    vocabulary="behavioral"
                    unit={unitOf(activeModel.type)}
                    title="Performance"
                    scope="Validation dataset"
                  />
                ) : (
                  <Row>
                    <ModelPerformanceGraphs
                      performances={validation.performances}
                      context={isCustomerFit ? 'customer_fit' : 'behavioral'}
                      page="performance"
                      cardTitleLead="Account and Conversion distribution by Segment"
                      colWidth={6}
                    />
                  </Row>
                ))}
              {performanceCheckData &&
                (revamped && isCustomerFit ? (
                  /* Unwrapped from the accordion and one panel lighter. The
                     breakdown that used to lead this section drew the same
                     leads, conversions and revenue split as the block at the top
                     of the page, so the page was showing it twice, once behind a
                     collapsed header. The Heads up alert is gone for the same
                     reason: the source note at the top already says these
                     numbers are a one shot analysis of the validation dataset
                     rather than the moving window the Predictions pages read. */
                  <Card className="pchk-card">
                    <Card.Body>
                      <h5 className="pchk-title">
                        <i
                          className="fas fa-coins pchk-icon"
                          aria-hidden="true"
                        />
                        Revenue and lead value
                      </h5>
                      <p className="pchk-scope">
                        {isLive
                          ? 'Still the validation dataset: revenue per lead is not part of what the live source carries, so this block does not follow the switch above.'
                          : 'The same population, counted on Closed Won conversions only.'}
                      </p>
                      <Row className="mt-3" ref={this.scrollAnchor}>
                        <Col md={6}>
                          <LeadToRevenueComponent
                            leadToRevenue={performanceCheckData.leadToRevenue}
                            segmentPalette
                          />
                        </Col>
                        <Col md={6}>
                          <PredictedLeadValue
                            data={performanceCheckData.predictedLeadValue}
                            tierBars
                          />
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ) : (
                  <>
                    <Accordion>
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>
                          Revenue and Lead value analysis
                        </Accordion.Header>
                        <Accordion.Body>
                          <p>
                            The population is the same as above, but looking at
                            Closed Won conversions.
                          </p>
                          <Alert>
                            <b>Heads up!</b> If this model is deployed, It is
                            expected to see a difference between these graphs
                            and the graphs in the Predictions monitoring pages
                            because the data is different. Here you find a one
                            shot static analysis based on the validation dataset
                            uploaded while the Predictions in the app are using
                            the moving last 6 months and therefore change
                            overtime.
                          </Alert>
                          <Row className="mb-2 mt-2" ref={this.scrollAnchor}>
                            <Col xs={4}>
                              <FunnelBreakdownComponent
                                funnelBreakdown={
                                  performanceCheckData.funnelBreakdown
                                }
                              />
                            </Col>
                            <Col xs={4}>
                              <LeadToRevenueComponent
                                leadToRevenue={
                                  performanceCheckData.leadToRevenue
                                }
                              />
                            </Col>
                            <Col xs={4}>
                              <PredictedLeadValue
                                data={performanceCheckData.predictedLeadValue}
                              />
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </>
                ))}
            </>
          </div>
        );
      }
    }
  )
);

export default withRouter(PerformanceCheckPage);
